/**
 * Shared scroll-direction chrome for floating nav compact + mobile header collapse.
 * Collapse after sustained downward scroll past a threshold; restore after sustained
 * upward scroll (optional near-bottom lock skips restore from overscroll bounce).
 */

export const SCROLL_CHROME_THRESHOLD = 24;
export const SCROLL_CHROME_DISTANCE = 18;
/** Default: any upward delta restores (nav pill). Header passes a larger value. */
export const SCROLL_CHROME_EXPAND_DISTANCE = 0;
/** Header: intentional up-scroll before the chrome returns. */
export const SCROLL_CHROME_HEADER_EXPAND_DISTANCE = 48;
export const SCROLL_BOTTOM_EXPAND_SLACK = 8;
/**
 * Deltas larger than this are treated as programmatic jumps (route remount /
 * scroll restore), not finger gestures — collapse state is left unchanged.
 */
export const SCROLL_CHROME_JUMP_THRESHOLD = 120;

export const SCROLL_CHROME_TARGET_SELECTOR =
	'.scroll-content-new-layout, .main-layout-scroll, .dashboard-scroll';

export type ScrollChromeTracker = {
	lastScrollTop: number;
	downwardDistance: number;
	upwardDistance: number;
	isCollapsed: boolean;
};

export function createScrollChromeTracker(): ScrollChromeTracker {
	return {
		lastScrollTop: 0,
		downwardDistance: 0,
		upwardDistance: 0,
		isCollapsed: false,
	};
}

export function isScrollNearBottom(
	el: HTMLElement,
	slack: number = SCROLL_BOTTOM_EXPAND_SLACK,
): boolean {
	return el.scrollHeight - el.scrollTop - el.clientHeight <= slack;
}

export type ResolveScrollChromeOptions = {
	enabled?: boolean;
	isMobile: boolean;
	forceExpanded?: boolean;
	threshold?: number;
	distance?: number;
	/** Cumulative upward px required before restoring. `0` = any uptick. */
	expandDistance?: number;
	/**
	 * When true and `scrollEl` is near the bottom, ignore upward deltas so
	 * overscroll / end-of-list bounce does not expand the chrome.
	 */
	lockExpandNearBottom?: boolean;
	scrollEl?: HTMLElement | null;
	/** Override for discontinuous jump detection (defaults to JUMP_THRESHOLD). */
	jumpThreshold?: number;
};

/**
 * Updates tracker and returns whether chrome should be collapsed/compact.
 * State is sticky: stays collapsed until expand rules fire (and vice versa).
 */
export function resolveScrollChromeCollapsed(
	tracker: ScrollChromeTracker,
	scrollTop: number,
	options: ResolveScrollChromeOptions,
): boolean {
	const {
		enabled = true,
		isMobile,
		forceExpanded = false,
		threshold = SCROLL_CHROME_THRESHOLD,
		distance = SCROLL_CHROME_DISTANCE,
		expandDistance = SCROLL_CHROME_EXPAND_DISTANCE,
		lockExpandNearBottom = false,
		scrollEl = null,
		jumpThreshold = SCROLL_CHROME_JUMP_THRESHOLD,
	} = options;

	const normalizedScrollTop = Math.max(0, scrollTop);

	if (!enabled || !isMobile || forceExpanded) {
		tracker.downwardDistance = 0;
		tracker.upwardDistance = 0;
		tracker.isCollapsed = false;
		tracker.lastScrollTop = normalizedScrollTop;
		return false;
	}

	const scrollDelta = normalizedScrollTop - tracker.lastScrollTop;

	// Route remount / scroll restore: resync without treating as a gesture.
	if (Math.abs(scrollDelta) > jumpThreshold) {
		tracker.downwardDistance = 0;
		tracker.upwardDistance = 0;
		tracker.lastScrollTop = normalizedScrollTop;
		return tracker.isCollapsed;
	}

	const nearBottom = lockExpandNearBottom && !!scrollEl && isScrollNearBottom(scrollEl);

	if (scrollDelta > 0) {
		tracker.downwardDistance += scrollDelta;
		tracker.upwardDistance = 0;
		if (normalizedScrollTop > threshold && tracker.downwardDistance > distance) {
			tracker.isCollapsed = true;
		}
	} else if (scrollDelta < 0) {
		tracker.downwardDistance = 0;
		if (nearBottom) {
			// Rubber-band / end bounce: keep collapsed, don't bank upward distance.
			tracker.upwardDistance = 0;
		} else {
			tracker.upwardDistance += -scrollDelta;
			if (tracker.upwardDistance > expandDistance) {
				tracker.isCollapsed = false;
			}
		}
	}

	tracker.lastScrollTop = normalizedScrollTop;
	return tracker.isCollapsed;
}

export function isScrollChromeTarget(target: EventTarget | null): target is HTMLElement {
	return target instanceof HTMLElement && !!target.closest(SCROLL_CHROME_TARGET_SELECTOR);
}
